import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ComingSoonOverlay } from "@/components/ui/coming-soon-overlay"
import { Bot, FileText, Gift, Image, Mail, MessageSquare, PenTool, Presentation, Sparkles } from "lucide-react"
import { useNavigate } from "react-router-dom"

interface ToolCard {
  title: string
  description: string
  icon: React.ReactNode
  comingSoon?: boolean
  path?: string
}

const tools: ToolCard[] = [
  {
    title: "Grant Writing",
    description: "AI-powered grant proposal generator with templates and best practices",
    icon: <PenTool className="h-5 w-5 text-purple-500" />,
    path: "/grant-writing"
  },
  {
    title: "Impact Report Generator",
    description: "Auto-generate comprehensive post-event and grant impact reports",
    icon: <FileText className="h-5 w-5 text-blue-500" />,
    path: "/impact-reports"
  },
  {
    title: "Fundraising Appeal Emails",
    description: "AI-crafted compelling emails to request donations and support",
    icon: <Mail className="h-5 w-5 text-pink-500" />,
    path: "/fundraising-emails"
  },
  {
    title: "Donor Thank You Emails",
    description: "Generate personalized gratitude messages for your supporters",
    icon: <Gift className="h-5 w-5 text-red-500" />,
    path: "/thank-you-emails"
  },
  {
    title: "Fundraising Campaign Images",
    description: "Create eye-catching visuals for your donation drives",
    icon: <Image className="h-5 w-5 text-green-500" />,
    path: "/campaign-images"
  },
  {
    title: "Social Media Templates",
    description: "Pre-structured donor engagement posts for maximum impact",
    icon: <MessageSquare className="h-5 w-5 text-orange-500" />,
    path: "/social-templates"
  },
  {
    title: "Pitch Deck Generator",
    description: "AI-assisted presentation decks for compelling grant applications",
    icon: <Presentation className="h-5 w-5 text-indigo-500" />,
    path: "/pitch-deck"
  }
]

export default function FundraisingAndGrantsPage({ profile, session }: { profile: any | null, session: any | null }) {
  const navigate = useNavigate()

  return (
    <div className="h-full w-full">
      {/* Content */}
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Bot className="h-8 w-8 text-purple-500" />
          <div>
            <h1 className="text-2xl font-bold">AI-Powered Fundraising & Grants</h1>
            <p className="text-muted-foreground">
              Supercharge your fundraising efforts with AI assistance
            </p>
          </div>
        </div>

        <div className="relative">
          {/* Coming Soon Overlay */}
          <ComingSoonOverlay message="AI-Powered Fundraising - Coming Soon" />
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {tools.map((tool) => (
              <Card
                key={tool.title}
                className="group relative overflow-hidden border border-border/50 bg-background/50 backdrop-blur-sm transition-all hover:border-purple-200 hover:shadow-lg"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="rounded-full bg-purple-50 p-2 transition-colors group-hover:bg-purple-100">
                      {tool.icon}
                    </div>
                    {tool.comingSoon && (
                      <span className="inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-2.5 py-0.5 text-xs font-medium text-purple-700">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <CardTitle className="text-xl">{tool.title}</CardTitle>
                  <CardDescription>{tool.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    className="w-full bg-gradient-to-r from-purple-500 to-blue-500 text-white transition-all hover:from-purple-600 hover:to-blue-600"
                    disabled={tool.comingSoon}
                    onClick={() => tool.path && navigate(tool.path)}
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    {tool.comingSoon ? "Coming Soon" : "Get Started"}
                  </Button>
                </CardContent>

                {/* Decorative corner gradients */}
                <div className="absolute -right-6 -top-6 h-12 w-12 rounded-full bg-gradient-to-br from-purple-200 to-transparent opacity-50 blur-lg transition-all group-hover:opacity-100" />
                <div className="absolute -bottom-6 -left-6 h-12 w-12 rounded-full bg-gradient-to-tr from-blue-200 to-transparent opacity-50 blur-lg transition-all group-hover:opacity-100" />
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
